// Browser entry point. Renders the flow state machine and dispatches into it.
//
// All the constrained behaviour lives in src/flow/state.js; this file is the
// DOM. Anything it decides for itself is presentation.

import { derive, initialState, reduce, requestBody, IN_FLIGHT_TIMEOUT_MS } from './flow/state.js';
import { CREDENTIAL_HEADER, readCredential, readReplaceEventId } from './flow/credential.js';
import { reportZone } from './flow/report-zone.js';
import { CUSTOM_NAME_MAX } from './shared/catalog.js';
import { HOME_ZONE, LOCATIONS, findLocation } from './shared/zones.js';
import { renderInZone } from './shared/render.js';
import { readClocks } from './flow/clocks.js';
import { catalogGuard, credentialPresenceGuard } from './flow/bootstrap.js';
import { bunny, mascotNode, monkey, sleepy } from './flow/mascot.js';
import { buildStickerLayer, layoutStickers, observeStickers } from './flow/stickers.js';

const root = document.querySelector('#app');
let state = initialState;
let stickerLayer = null;
/** What Discord says is already booked. Null until the first fetch returns. */
let upcoming = null;

// Arriving from a "Change the date" link means she has already been asked and
// already said yes, so this skips the asking as well as recording what is being
// replaced.
//
// It has to run BEFORE the first render: nothing re-renders on its own
// afterwards, so state applied later never reaches the screen.
const arrivedReplacing = readReplaceEventId();
if (arrivedReplacing !== null) {
  state = reduce(state, { type: 'replaceEvent', eventId: arrivedReplacing });
}

/**
 * Positions depend on what the content currently occupies, and that differs
 * completely between the asking and the flow — so it runs after every render,
 * once layout has settled.
 */
function placeStickers() {
  if (!stickerLayer) return;
  requestAnimationFrame(() => layoutStickers(stickerLayer, root));
}

/**
 * replaceChildren stringifies anything that is not a Node, so a null child
 * becomes a text node reading "null" on the page. el() filters falsy children
 * already; this is for the top-level calls that do not go through it.
 */
const show = (...nodes) => root.replaceChildren(...nodes.filter(Boolean));

const el = (tag, props = {}, children = []) => {
  const node = Object.assign(document.createElement(tag), props);
  for (const child of [].concat(children)) {
    if (child) node.append(child.nodeType ? child : document.createTextNode(String(child)));
  }
  return node;
};

/** The three mice, for when it worked. */
function yayMascot() {
  return el('img', { className: 'mascot-img mascot-img--wide', src: './mascot-yay.png', alt: '' });
}

/** The waving mouse, for when something did not work out. */
function oopsMascot() {
  return el('img', { className: 'mascot-img mascot-img--sm', src: './mascot-oops.png', alt: '' });
}

function notice(heading, body, mascot = sleepy) {
  show(el('div', { className: 'panel' }, [
    mascotNode(mascot),
    el('h1', { className: 'notice' }, heading),
    el('p', {}, body),
  ]));
}

// Both guards run on every page load, not only in tests.
const catalog = catalogGuard();
const credential = credentialPresenceGuard();

if (!catalog.ok) {
  notice(catalog.heading, catalog.message);
} else if (!credential.ok) {
  notice('This link is missing a bit', credential.message, monkey);
} else {
  // Built once, outside the render cycle: rebuilding on every tap would make
  // the scatter flicker and reshuffle.
  // The artwork layer. Harmless when src/background.png does not exist — the
  // element paints nothing and the pastel blobs behind it carry the design.
  const artwork = document.createElement('div');
  artwork.className = 'artwork';
  artwork.setAttribute('aria-hidden', 'true');
  document.body.prepend(artwork);

  // Tell the server which clock she is on, for the hydration reminders. This
  // does not affect how her picks are read — those come from the location she
  // taps. See src/flow/report-zone.js.
  reportZone();

  // Ask Discord what is already booked. Failure is silent on purpose: not
  // knowing makes for a poorer page, not a broken one, and she can still book.
  fetch('/api/upcoming', { headers: { [CREDENTIAL_HEADER]: readCredential() ?? '' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((body) => {
      if (body?.outcome === 'ok' && Array.isArray(body.events)) {
        upcoming = body.events;
        render();
      }
    })
    .catch(() => {});

  // Clocks only change once a minute; ten seconds looks live without
  // re-rendering the whole page on a timer.
  setInterval(() => {
    for (const strip of document.querySelectorAll('.ticks')) {
      strip.replaceWith(clockStrip(state.locationId));
    }
  }, 10_000);

  stickerLayer = buildStickerLayer();
  document.body.prepend(stickerLayer);
  observeStickers(stickerLayer);
  render();

  // The free space changes shape with the window, so re-measure on resize.
  let resizing;
  addEventListener('resize', () => {
    clearTimeout(resizing);
    resizing = setTimeout(placeStickers, 150);
  });
}

function dispatch(action) {
  state = reduce(state, action);
  render();
}

/** The three clocks, ticking. */
function clockStrip(locationId) {
  return el('div', { className: 'ticks' },
    readClocks(locationId).map((face) =>
      el('span', { className: `tick${face.mine ? ' tick--hers' : ''}` }, [
        el('span', { className: 'tick__label', textContent: face.label }),
        el('span', { className: 'tick__time', textContent: `${face.time}${face.meridiem}` }),
      ])));
}

/**
 * What is already booked, if anything. Stops her quietly booking a second date
 * night on top of one neither of you had noticed, and gives the page something
 * to say when she opens it.
 */
/**
 * The already-booked date night — or, when she is changing it, the one she is
 * changing. Naming it matters: "this will replace the one already booked" does
 * not say which, and she may well have forgotten what was booked.
 */
function bookedPanel(view) {
  if (!upcoming || upcoming.length === 0) return null;
  const replacing = view.replaceEventId
    ? upcoming.find((event) => event.eventId === view.replaceEventId)
    : null;
  const next = replacing ?? upcoming[0];
  // Changing something that is not in the list — deleted, or the fetch has not
  // landed yet. The header subtitle covers that case instead.
  if (view.replaceEventId && !replacing) return null;

  // The event carries her location, so it can be shown in her own clock. One
  // booked before that was recorded has none — then say less rather than guess.
  const found = next.locationId ? findLocation(next.locationId) : { found: false };
  const hers = found.found ? renderInZone(next.instant, found.location.zone) : null;
  const mine = renderInZone(next.instant, HOME_ZONE);

  const days = Math.round((new Date(next.instant).getTime() - Date.now()) / 86_400_000);
  const away = days <= 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;

  // Her time, not his — she is the one reading this. When the event predates
  // location recording there is no city on it, but there are only ever two
  // candidates, so both are shown rather than falling back to his clock.
  const when = hers
    ? `${hers.weekday} ${hers.time12}`
    : LOCATIONS.map((l) => `${renderInZone(next.instant, l.zone).time12} in ${l.label}`).join(' · ');

  return el('section', { className: `booked${replacing ? ' booked--replacing' : ''}` }, [
    el('p', {
      className: 'booked__eyebrow',
      textContent: replacing ? 'Replacing this one' : `Already booked · ${away}`,
    }),
    el('p', { className: 'booked__name', textContent: next.name }),
    el('p', { className: 'booked__when', textContent: when }),
    hers ? null : el('p', { className: 'booked__note', textContent: `${mine.weekday} ${mine.time12} for me` }),
    el('div', { className: 'booked__actions' }, replacing
      ? [
          el('button', {
            type: 'button', className: 'link',
            textContent: 'Never mind, keep this one',
            onclick: () => dispatch({ type: 'cancelReplace' }),
          }),
        ]
      : [
          el('button', {
            type: 'button',
            className: 'button booked__change',
            textContent: 'Change the date',
            onclick: () => dispatch({ type: 'replaceEvent', eventId: next.eventId }),
          }),
          el('a', { className: 'link', href: next.eventUrl, textContent: 'Open it in Discord' }),
        ]),
  ]);
}

function step(index, title, body, { enabled = true } = {}) {
  return el('section', { className: `step${enabled ? '' : ' step--inert'}` }, [
    el('h2', {}, [el('span', { className: 'num', textContent: String(index) }), title]),
    body,
  ]);
}

function locationStep(view) {
  const options = view.locations.map((location) =>
    el('button', {
      type: 'button',
      className: `chip${view.locationId === location.id ? ' chip--on' : ''}`,
      textContent: location.label,
      onclick: () => dispatch({ type: 'selectLocation', locationId: location.id }),
    }),
  );
  return step(1, 'Where are you right now?', el('div', { className: 'chips' }, options));
}

function activityStep(view) {
  const cards = view.activities.map((activity) =>
    el('button', {
      type: 'button',
      disabled: !view.stepsEnabled,
      className: `card${view.activityId === activity.id ? ' card--on' : ''}`,
      onclick: () => dispatch({ type: 'selectActivity', activityId: activity.id }),
    }, [
      // Name only. The duration still sets the event's end time; it is simply
      // not something she needs to read while choosing.
      el('span', { className: 'card__name', textContent: activity.name }),
    ]),
  );
  const written = view.isCustom
    ? el('input', {
        type: 'text',
        className: 'field custom',
        placeholder: 'Tell me what you fancy…',
        maxLength: CUSTOM_NAME_MAX,
        value: view.customActivity,
        disabled: !view.stepsEnabled,
        // 'input' so the submit button unlocks as she types, rather than only
        // once the field loses focus.
        oninput: (event) => dispatch({ type: 'setCustomActivity', text: event.target.value }),
      })
    : null;

  // Keep the caret where it was: the whole step is rebuilt on every keystroke,
  // so without this the field would lose focus after the first character.
  if (written) queueMicrotask(() => {
    const live = root.querySelector('.custom');
    if (live && document.activeElement !== live) {
      live.focus();
      live.setSelectionRange(live.value.length, live.value.length);
    }
  });

  return step(2, 'What do you wanna do?', el('div', {}, [
    el('div', { className: 'cards' }, cards),
    written,
  ]), { enabled: view.stepsEnabled });
}

function dayStep(view) {
  const { calendar } = view;
  if (!calendar) return step(3, 'What day?', el('div', {}), { enabled: false });

  const arrow = (label, delta, enabled) =>
    el('button', {
      type: 'button',
      className: 'cal__arrow',
      disabled: !enabled,
      'aria-label': label,
      textContent: delta < 0 ? '‹' : '›',
      onclick: () => dispatch({ type: 'shiftMonth', delta }),
    });

  const head = el('div', { className: 'cal__head' }, [
    arrow('Previous month', -1, calendar.canGoBack),
    el('span', { className: 'cal__month', textContent: calendar.label }),
    arrow('Next month', 1, calendar.canGoForward),
  ]);

  const names = el('div', { className: 'cal__row cal__row--names' },
    calendar.weekdays.map((d) => el('span', { className: 'cal__name', textContent: d })));

  const grid = calendar.weeks.map((week) =>
    el('div', { className: 'cal__row' }, week.map((cell) => {
      const classes = ['cal__day'];
      if (!cell.inMonth) classes.push('cal__day--faint');
      if (cell.selected) classes.push('cal__day--on');
      if (cell.isToday) classes.push('cal__day--today');
      return el('button', {
        type: 'button',
        className: classes.join(' '),
        disabled: !cell.selectable,
        textContent: String(cell.day),
        onclick: () => dispatch({ type: 'selectDay', date: cell.iso }),
      });
    })));

  return step(3, 'What day?', el('div', { className: 'cal' }, [head, names, ...grid]),
    { enabled: view.stepsEnabled });
}

const WHEEL_ITEM = 44;   // px per row; must match --wheel-item in the stylesheet

function timeStep(view) {
  const parts = view.timeParts ?? { hour: 20, minute: 0, hour12: 8, meridiem: 'PM' };
  const isSet = view.timeParts !== null;
  const pad = (n) => String(n).padStart(2, '0');

  const readout = el('div', { className: 'time__display' }, [
    el('span', {
      className: `time__big${isSet ? '' : ' time__big--empty'}`,
      textContent: isSet ? `${parts.hour12}:${pad(parts.minute)} ${parts.meridiem}` : '--:--',
    }),
    el('span', { className: 'time__aside', textContent: 'your clock' }),
  ]);

  /**
   * One scrolling column. Snap-scrolling does the hard part; the only real work
   * is committing on settle rather than on every scroll event — a dispatch
   * mid-scroll would re-render the page and yank the wheel out from under her.
   */
  function wheel(values, selectedIndex, onPick, label) {
    // Each row is tappable as well as scrollable. Scrolling is quicker across
    // a long column; tapping is quicker when the value you want is already on
    // screen, and it does not ask for a precise flick.
    const items = values.map((value, i) => el('div', {
      className: `wheel__item${i === selectedIndex ? ' wheel__item--on' : ''}`,
      role: 'option',
      'aria-selected': i === selectedIndex ? 'true' : 'false',
      textContent: String(value),
    }));

    const column = el('div', {
      className: 'wheel',
      role: 'listbox',
      'aria-label': label,
      tabIndex: view.stepsEnabled ? 0 : -1,
    }, [
      el('div', { className: 'wheel__pad' }),
      ...items,
      el('div', { className: 'wheel__pad' }),
    ]);

    if (!view.stepsEnabled) return column;

    items.forEach((node, i) => {
      node.classList.add('wheel__item--tappable');
      node.addEventListener('click', () => {
        if (i === selectedIndex) return;
        // Dispatch straight away; the rebuilt wheel positions itself on the
        // new value, so the tap reads as instant.
        onPick(values[i], i);
      });
    });

    let settle;
    const highlight = () => {
      const i = Math.round(column.scrollTop / WHEEL_ITEM);
      items.forEach((node, n) => node.classList.toggle('wheel__item--on', n === i));
      return i;
    };
    column.addEventListener('scroll', () => {
      highlight();
      clearTimeout(settle);
      settle = setTimeout(() => {
        const i = Math.max(0, Math.min(values.length - 1, Math.round(column.scrollTop / WHEEL_ITEM)));
        if (i !== selectedIndex) onPick(values[i], i);
      }, 140);
    }, { passive: true });

    // Keyboard, since a scroll container is otherwise unreachable without one.
    column.addEventListener('keydown', (event) => {
      const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
      if (!step) return;
      event.preventDefault();
      const i = Math.max(0, Math.min(values.length - 1, selectedIndex + step));
      onPick(values[i], i);
    });

    // Jump to the current value without animating on first paint.
    requestAnimationFrame(() => { column.scrollTop = selectedIndex * WHEEL_ITEM; });
    return column;
  }

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => pad(i));

  const columns = el('div', { className: 'wheels' }, [
    wheel(hours, parts.hour12 - 1, (v) => dispatch({ type: 'setHour12', hour12: v }), 'Hour'),
    el('span', { className: 'wheels__colon', textContent: ':' }),
    wheel(minutes, parts.minute, (_, i) => dispatch({ type: 'setMinute', minute: i }), 'Minute'),
    wheel(['AM', 'PM'], parts.meridiem === 'PM' ? 1 : 0,
      (v) => dispatch({ type: 'setMeridiem', meridiem: v }), 'AM or PM'),
  ]);

  return step(4, 'What time?', el('div', { className: 'time' }, [
    readout,
    el('div', { className: 'wheels__frame' }, [
      el('div', { className: 'wheels__band' }),
      columns,
    ]),
  ]), { enabled: view.stepsEnabled });
}

function clockLine(rendering, label) {
  return el('div', { className: 'clock' }, [
    el('span', { className: 'clock__time', textContent: rendering.time12 }),
    el('span', { className: 'clock__rest', textContent: `${rendering.weekday} ${rendering.date} · ${label}` }),
  ]);
}

function summary(view) {
  if (view.refusal) {
    const messages = {
      nonexistent: view.refusal.nearest
        ? `That time does not exist that day — the clocks jump from ${view.refusal.nearest.before.slice(11, 16)} to ${view.refusal.nearest.after.slice(11, 16)}. Pick another time.`
        : 'That time does not exist that day.',
      'too-soon': 'That time has already gone by — pick a time from now on.',
      'too-far': `That is past the last day we can book, ${view.refusal.lastDay}.`,
      invalid: 'That day and time do not make sense together.',
    };
    return el('p', { className: 'refusal', textContent: messages[view.refusal.kind] });
  }
  if (!view.clocks) return null;

  return el('div', { className: 'summary' }, [
    clockLine(view.clocks.hers, 'you'),
    clockLine(view.clocks.home, 'me'),
    view.ambiguous
      ? el('p', { className: 'hint', textContent: 'The clocks go back that night, so this time happens twice. I have taken the earlier one.' })
      : null,
  ]);
}

function outcomePanel(view) {
  const { outcome } = view;
  if (outcome.kind === 'scheduled') {
    return el('div', { className: 'panel panel--good' }, [
      yayMascot(),
      el('h1', {}, 'It is a date!'),
      outcome.clocks ? clockLine(outcome.clocks.hers, 'you') : null,
      outcome.clocks ? clockLine(outcome.clocks.home, 'me') : null,
      outcome.ambiguous
        ? el('p', { className: 'hint', textContent: 'That time happened twice — the earlier one was used.' })
        : null,
      outcome.announced
        ? el('p', {}, 'Jonathan has been pinged in Discord.')
        : el('p', { className: 'warn' }, 'The event exists, but the Discord message did not go out — he may not have been notified.'),
      el('a', { className: 'button', href: outcome.eventUrl, textContent: 'Open it in Discord' }),
      el('button', {
        type: 'button', className: 'link', textContent: 'Pick another one',
        onclick: () => dispatch({ type: 'scheduleAnother' }),
      }),
    ]);
  }
  if (outcome.kind === 'rejected') {
    return el('div', { className: 'panel panel--bad' }, [
      oopsMascot(),
      el('h1', {}, 'That did not go through'),
      el('p', {}, outcome.message),
    ]);
  }
  return el('div', { className: 'panel panel--unsure' }, [
    oopsMascot(),
    el('h1', {}, 'I am not sure what happened'),
    el('p', {}, outcome.message),
  ]);
}

async function submit() {
  dispatch({ type: 'submitRequested' });
  if (state.status !== 'inflight') return;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), IN_FLIGHT_TIMEOUT_MS);
  try {
    const response = await fetch('/api/schedule', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // The credential travels as a header. Never a query parameter, never
        // in the body: those are logged and forwarded, the fragment is not.
        [CREDENTIAL_HEADER]: readCredential() ?? '',
      },
      body: JSON.stringify(requestBody(state)),
      signal: controller.signal,
    });
    const answer = await response.json().catch(() => null);
    dispatch({ type: 'answerReceived', answer });
  } catch {
    // No answer arrived. Unknown — never "not scheduled".
    dispatch({ type: 'requestFailed' });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The asking. The No button never actually declines.
 *
 * It dodges on *proximity* rather than on contact: waiting for pointerenter
 * means the cursor has already arrived, and waiting for pointerdown means she
 * has already pressed. Watching pointermove across the panel lets it leave
 * before either happens, which is the whole joke.
 *
 * Touch has neither hover nor approach, so pointerdown is still caught and
 * cancelled there — on a finger the press has to be dodged as it lands.
 */
function welcomeScreen() {
  let dodges = 0;

  const no = el('button', {
    type: 'button',
    className: 'ask__no',
    textContent: 'No',
  });

  function moveAway(awayFrom) {
    dodges += 1;
    const box = no.getBoundingClientRect();
    const scale = Math.max(0.45, 1 - dodges * 0.06);

    // Pick a spot, preferring one away from wherever the pointer is, and keep
    // it on screen — a button that flees off the edge is just gone.
    let best = null;
    for (let i = 0; i < 12; i += 1) {
      const x = (Math.random() * 2 - 1) * 38;
      const y = (Math.random() * 2 - 1) * 24;
      const cx = box.left + box.width / 2 + (x / 100) * innerWidth;
      const cy = box.top + box.height / 2 + (y / 100) * innerHeight;
      if (cx < 60 || cx > innerWidth - 60 || cy < 60 || cy > innerHeight - 60) continue;
      const far = awayFrom ? Math.hypot(cx - awayFrom.x, cy - awayFrom.y) : Math.random();
      if (!best || far > best.far) best = { x, y, far };
    }
    const spot = best ?? { x: 0, y: 0 };
    no.style.transform = `translate(${spot.x}vw, ${spot.y}vh) scale(${scale})`;

    if (dodges === 3) no.textContent = 'no?';
    if (dodges === 6) no.textContent = 'nope';
    if (dodges >= 9) no.textContent = ':(';
  }

  // Approach detection. Throttled to one dodge per settle, so a single sweep
  // of the mouse does not fire it a dozen times.
  let cooling = false;
  const onMove = (event) => {
    if (cooling) return;
    const box = no.getBoundingClientRect();
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    const near = Math.hypot(event.clientX - cx, event.clientY - cy)
      < Math.max(box.width, box.height) * 0.9 + 40;
    if (!near) return;
    cooling = true;
    moveAway({ x: event.clientX, y: event.clientY });
    setTimeout(() => { cooling = false; }, 220);
  };

  // Touch: no hover, no approach — cancel the press itself.
  const onPress = (event) => {
    event.preventDefault();
    moveAway({ x: event.clientX ?? 0, y: event.clientY ?? 0 });
  };
  no.addEventListener('pointerdown', onPress);
  no.addEventListener('touchstart', onPress, { passive: false });
  no.addEventListener('focus', () => moveAway(null));

  const yes = el('button', {
    type: 'button',
    className: 'button ask__yes',
    textContent: 'Yes',
    onclick: () => dispatch({ type: 'accept' }),
  });

  const panel = el('div', { className: 'panel ask' }, [
    el('img', { className: 'mascot-img', src: './mascot.png', alt: '' }),
    el('h1', {}, 'Will you go on a date with me?'),
    el('div', { className: 'ask__buttons' }, [yes, no]),
  ]);

  // On the document, not the panel: once it has fled the panel's bounds, the
  // panel would stop hearing about the cursor.
  document.addEventListener('pointermove', onMove);

  return panel;
}

function render() {
  const view = derive(state);

  if (!view.accepted && !view.outcome) {
    show(welcomeScreen());
    placeStickers();
    return;
  }

  if (view.outcome) {
    show(outcomePanel(view));
    placeStickers();
    return;
  }

  const submitButton = el('button', {
    type: 'button',
    className: 'button',
    disabled: !view.canSubmit,
    textContent: view.status === 'inflight' ? 'Just a moment…' : 'It is a date!',
    onclick: submit,
  });

  const replacing = view.replaceEventId;

  show(
    el('header', {}, [
      clockStrip(view.locationId),
      el('img', { className: 'mascot-img', src: './mascot.png', alt: '', width: 544, height: 312 }),
      el('h1', {}, 'Our next date night'),
      el('p', {}, replacing && !(upcoming ?? []).some((e) => e.eventId === replacing)
        // The panel names the one being replaced whenever it can; this covers
        // the case where it cannot.
        ? 'Changing the date \u2014 this will replace the one already booked'
        : 'Pick whatever you want, I\u2019ll be there :)'),
    ]),
    bookedPanel(view),
    locationStep(view),
    activityStep(view),
    dayStep(view),
    timeStep(view),
    el('section', { className: 'step' }, [summary(view), submitButton]),
  );

  placeStickers();
}
